import React, { useState } from 'react';
import { Check, X, AlertCircle, Loader2 } from 'lucide-react';

// Reusable status badge component
export const EventStatusBadge = ({ status, approvalDetails }) => {
  const getStatusConfig = () => {
    const configs = {
      draft: {
        color: 'bg-gray-100 text-gray-800',
        label: 'Draft'
      },
      pending_approval: {
        color: 'bg-yellow-100 text-yellow-800',
        label: 'Pending Approval'
      },
      approved: {
        color: 'bg-green-100 text-green-800',
        label: 'Approved'
      },
      rejected: {
        color: 'bg-red-100 text-red-800',
        label: 'Rejected'
      },
      published: {
        color: 'bg-blue-100 text-blue-800',
        label: 'Published'
      },
      cancelled: {
        color: 'bg-gray-100 text-gray-800',
        label: 'Cancelled'
      },
      completed: {
        color: 'bg-purple-100 text-purple-800',
        label: 'Completed'
      }
    };

    return configs[status] || configs.draft;
  };

  const config = getStatusConfig();

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

// Approval actions component with comment modal
export const ApprovalActions = ({ event, onApprove, onReject }) => {
  const [showModal, setShowModal] = useState(false);
  const [comment, setComment] = useState('');
  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAction = async () => {
    setLoading(true);
    try {
      if (action === 'approve') {
        await onApprove(comment);
      } else {
        await onReject(comment);
      }
      setShowModal(false);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (actionType) => {
    setAction(actionType);
    setComment('');
    setShowModal(true);
  };

  return (
    <>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => openModal('approve')}
          className="inline-flex items-center px-3 py-1.5 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
        >
          <Check className="w-4 h-4 mr-1.5" />
          Approve
        </button>
        <button
          onClick={() => openModal('reject')}
          className="inline-flex items-center px-3 py-1.5 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
        >
          <X className="w-4 h-4 mr-1.5" />
          Reject
        </button>
      </div>

      {/* Approval/Rejection Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {action === 'approve' ? 'Approve Event' : 'Reject Event'}
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comment
              </label>
              <textarea
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add a comment (optional)"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleAction}
                disabled={loading}
                className={`px-4 py-2 rounded-md text-white ${
                  action === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50 inline-flex items-center`}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : action === 'approve' ? (
                  <Check className="w-4 h-4 mr-2" />
                ) : (
                  <X className="w-4 h-4 mr-2" />
                )}
                {action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Event approval history component
export const ApprovalHistory = ({ approvalDetails }) => {
  if (!approvalDetails) return null;

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <h4 className="text-sm font-medium text-gray-900 mb-2">Approval History</h4>
      <div className="space-y-2">
        <div className="flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-gray-400" />
          <div>
            <p className="text-sm text-gray-600">
              Status: <span className="font-medium">{approvalDetails.status}</span>
            </p>
            {approvalDetails.approvedAt && (
              <p className="text-sm text-gray-500">
                {new Date(approvalDetails.approvedAt).toLocaleDateString()}
              </p>
            )}
            {approvalDetails.comment && (
              <p className="text-sm text-gray-600 mt-1">{approvalDetails.comment}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};